import { supabase } from "@/integrations/supabase/client";

export const STATEMENT_ARTIFACT_BUCKET = "giving-statements";
export const STATEMENT_DOWNLOAD_TTL_SECONDS = 300;

export function isPrivateStatementArtifact(organizationId: string, bucket: unknown, path: unknown): path is string {
  return bucket === STATEMENT_ARTIFACT_BUCKET && typeof path === "string"
    && path.length <= 1024 && path.startsWith(`${organizationId}/statements/`)
    && !path.includes("\\") && !path.split("/").some((part) => !part || part === "." || part === "..");
}

/** Read the authorized record again; never sign a locator supplied by a caller. */
export async function getGivingStatementDownloadUrl(organizationId: string, statementId: string): Promise<string> {
  const { data, error } = await supabase.from("giving_statements")
    .select("id,organization_id,artifact_bucket,artifact_path")
    .eq("organization_id", organizationId).eq("id", statementId).single();
  if (error) throw error;
  if (!data || !isPrivateStatementArtifact(organizationId, data.artifact_bucket, data.artifact_path)) {
    throw new Error("This statement needs an administrator to restore its private download.");
  }
  const result = await supabase.storage.from(STATEMENT_ARTIFACT_BUCKET)
    .createSignedUrl(data.artifact_path, STATEMENT_DOWNLOAD_TTL_SECONDS);
  if (result.error) throw result.error;
  if (!result.data?.signedUrl) throw new Error("The statement download is unavailable. Try again.");
  return result.data.signedUrl;
}

/** Open synchronously during the click, then authorize before navigating. */
export async function openGivingStatementDownload(organizationId: string, statementId: string): Promise<void> {
  const target = window.open("about:blank", "_blank");
  if (!target) throw new Error("Allow popups to download your statement, then try again.");
  try {
    target.opener = null;
    const referrer = target.document.createElement("meta");
    referrer.name = "referrer";
    referrer.content = "no-referrer";
    target.document.head.append(referrer);
    const url = await getGivingStatementDownloadUrl(organizationId, statementId);
    target.location.replace(url);
  } catch (error) {
    target.close();
    throw error;
  }
}
